import { SetStateAction, useEffect, useState } from "react";
import { useGeneralApiCall } from "@/services/useGeneralApiCall";
import { CategoryNode } from "@/types";

interface SelectApparelCategoryProps {
  categoryId: string | null;
  setCategoryId: React.Dispatch<React.SetStateAction<string | null>>;
  editCategory?: any;
  setCategory?: (category: CategoryNode | null) => void;
  addCategory?: boolean;
}

const SelectApparelCategory: React.FC<SelectApparelCategoryProps> = ({
  categoryId = "",
  setCategoryId,
  editCategory,
  setCategory,
  addCategory = false,
}) => {
  const { getApi } = useGeneralApiCall();
  const [categoriesData, setCategoriesData] = useState<any>([]);
  const [path, setPath] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<any>(null);
  const isRoot = categoryId ? false : true;

  const fetchCategoryList = async () => {
    try {
      const res = await getApi<SetStateAction<any>>(
        `api/msr/v1/combined-categories/`
      );
      if (!res.error_status) {
        setCategoriesData(res.data);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  useEffect(() => {
    fetchCategoryList();
  }, []);

  const findCategoryPath = (
    categories: any[],
    targetId: string,
    currentPath: any[] = []
  ): any[] | null => {
    for (const category of categories) {
      if (category.category_id === targetId) {
        return [...currentPath, category];
      }
      if (category.subcategories) {
        const found = findCategoryPath(category.subcategories, targetId, [
          ...currentPath,
          category,
        ]);
        if (found) return found;
      }
    }
    return null;
  };

  useEffect(() => {
    if (!categoriesData || !categoriesData.Apparel) return;

    if (categoryId) {
      const apparelRoot = {
        category_id: "CAT_APPAREL",
        name: "Apparel",
        subcategories: categoriesData.Apparel,
      };
      const found = findCategoryPath(
        apparelRoot.subcategories || [],
        selectedCategory ? selectedCategory.category_id : categoryId,
        [apparelRoot]
      );
      if (found) setPath(found);
    }

    // if (setCategory) {
    //   setCategory(selectedCategory);
    // }
  }, [categoriesData, categoryId, selectedCategory]);

  const handleSelect = (category: any, level: any) => {
    const newPath = path.slice(0, level);
    newPath[level] = category;
    setPath(newPath);
    setSelectedCategory(category);
    setCategoryId(category?.category_id);

    if (setCategory) {
      if (category.subcategories.length === 0) {
        const selectedCategory = {
          id: category.category_id,
          name: category.name,
          children: [],
          isLeaf: true,
        };
        setCategory(selectedCategory);
      } else {
        setCategory(null);
      }
    }
  };

  const getChildren = (level: any) => {
    // For root level (level 0)
    if (level === 0) {
      // If no categoryId (root parent case), show all root categories except selected one
      if (!categoryId) {
        return (
          categoriesData?.Apparel?.filter(
            (cat: any) => cat.category_id !== editCategory?.cat_id
          ) ?? []
        );
      }
    }

    // for the nested categories
    const parent = path[level - 1];
    return parent?.subcategories ?? [];
  };

  const handleRootChange = () => {
    setCategoryId(null);
    setSelectedCategory(null);
    if (setCategory) setCategory(null);
  };

  return (
    <div className="flex flex-col gap-2 my-2">
      <h5 className="label-style !text-[30px]">Select Apparel Category</h5>
      <div className="flex gap-4 flex-wrap">
        {Array.from({
          length: path.length + 1,
        }).map((_, level) => {
          const items = getChildren(level).filter(
            (cat: any) => cat?.category_id !== editCategory?.cat_id
          );

          if (!items || items.length === 0) return null;

          return (
            <div
              key={level}
              style={{
                border: "1px solid #ccc",
                borderRadius: "4px",
                padding: "0.5rem",
                minWidth: "180px",
                minHeight: "200px",
              }}
              className="h-[20rem] overflow-auto"
            >
              {items.map((cat: any) => (
                <div
                  key={cat?.category_id}
                  style={{
                    padding: "0.5rem",
                    borderRadius: "4px",
                    cursor: "pointer",
                    backgroundColor:
                      path[level]?.category_id === cat?.category_id
                        ? "#cce5ff"
                        : "transparent",
                    fontWeight:
                      path[level]?.category_id === cat?.category_id
                        ? "bold"
                        : "normal",
                  }}
                  onClick={() => handleSelect(cat, level)}
                >
                  {cat?.name}
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {!isRoot && !addCategory && (
        <div className="form-check mb-2">
          <button
            className="btn btn-danger"
            onClick={handleRootChange}
            type="button"
          >
            Remove Parent
          </button>
        </div>
      )}
    </div>
  );
};

export default SelectApparelCategory;
